<template>
    <div class="relative">
        <div v-if="inputType === 'dropdown-code-single'">
            <MultiSelect
                ref="multiselect"
                v-model="localValue"
                mode="single"
                select-label=""
                deselect-label=""
                :placeholder="getDropdownSinglePlaceholder()"
                track-by="id"
                label="label"
                value-prop="id"
                :attrs="{
                    autocomplete: 'off',
                }"
                :loading="isLoading"
                :options="codesProvider"
                :classes="styling"
                :searchable="true"
                :min-chars="0"
                :delay="250"
                :clear-on-search="true"
                :clear-on-select="true"
                :close-on-select="true"
                :clear-on-blur="false"
                :resolve-on-load="hasDropdownSingleSelection()"
                :can-clear="false"
                :can-deselect="false"
                :hide-selected="false"
                :no-results-text="noResultsText"
                :filter-results="false"
                :caret="true"
                :allow-absent="true"
                :disabled="disabled"
                no-options-text="No matching results found"
                @close="resetDropdownScroll"
                @open="handleInternalOpen"
            >
                <template #caret>
                    <div>
                        <div v-if="isLoading" class="absolute right-0 top-0 mr-3 flex h-full">
                            <AppIcon
                                name="spinner-third"
                                class="my-auto h-3.5 w-3.5 animate-spin text-slate-500"
                            />
                        </div>
                        <div
                            v-else
                            class="absolute right-0 top-0 mr-3 flex h-full"
                            @mousedown.stop.prevent="toggleMultiSelect()"
                        >
                            <AppIcon
                                name="chevron-down"
                                class="my-auto h-3 w-3 text-slate-500"
                            />
                        </div>
                    </div>
                </template>

                <template #option="{ option }">
                    <div class="flex w-full border-b border-slate-200 px-3">
                        <div class="flex min-w-0 flex-1 flex-col py-2 text-left">
                            <div class="w-full truncate font-medium text-slate-900">
                                {{ getCodeOptionDisplayLabel(option) }}
                            </div>
                        </div>
                    </div>
                </template>
            </MultiSelect>
        </div>

        <div v-if="inputType === 'select-code-multiple'">
            <slot name="selected-items">
                <div v-if="selectedItemsByNameAlphabetically?.length > 0" class="mb-1 flex flex-col">
                    <div
                        v-for="selectedItem in selectedItemsByNameAlphabetically"
                        :key="
                            codeType === 'games_list' ? `${selectedItem.id}-${selectedItem.metadata}` : selectedItem.id
                        "
                        class="mb-1 flex justify-between overflow-hidden rounded bg-slate-700"
                        :class="{
                            'relative overflow-hidden': selectedItem.background_url,
                        }"
                    >
                        <div
                            v-if="selectedItem.background_url"
                            class="absolute z-0 h-full w-full bg-cover bg-center opacity-10"
                            :style="{
                                'background-image': `url(${selectedItem.background_url})`,
                            }"
                        />

                        <div class="z-10 my-auto px-2.5 py-1.5 text-sm font-medium text-white">
                            <span>{{ getCodeOptionDisplayLabel(selectedItem) }}</span>
                            <span v-if="selectedItem?.metadata?.release_year" class="ml-0.5"
                                >({{ selectedItem.metadata.release_year }})</span
                            >
                        </div>

                        <div
                            v-if="selectedItem.value2"
                            class="z-10 my-auto ml-auto line-clamp-1 px-2.5 py-1.5 text-sm font-medium text-white"
                        >
                            ({{ sliderOptions.tooltipFormatter(selectedItem.value2) }})
                        </div>

                        <div class="mr-1.5 flex">
                            <div
                                class="my-auto cursor-pointer rounded-sm p-0.5 text-white opacity-80 hover:bg-black hover:bg-opacity-20 hover:opacity-70"
                                @click="onRemoveSelection(selectedItem)"
                            >
                                <AppIcon name="xmark" class="h-4 w-4" />
                            </div>
                        </div>
                    </div>
                </div>
            </slot>

            <div v-if="sliderOptions" class="pb-8">
                <vue-slider
                    v-if="sliderOptions.type == 'slider-range'"
                    v-model="sliderValue"
                    :min="1"
                    :max="100"
                    :data="sliderOptions.data"
                    :marks="sliderOptions.marks"
                    :enable-cross="false"
                    :tooltip="'none'"
                    :tooltip-placement="'bottom'"
                    :interval="5"
                    :clickable="true"
                />

                <vue-slider
                    v-if="sliderOptions.type == 'slider-affinity'"
                    v-model="sliderValue"
                    :data="sliderOptions.data"
                    :marks="true"
                    :enable-cross="false"
                    :lazy="true"
                    :tooltip="'none'"
                    :absorb="true"
                    :clickable="false"
                    :interval="0.1"
                    :min-range="0.1"
                    :fixed="false"
                    :contained="false"
                    class="mb-3"
                >
                    <template #mark="{ pos, label, active }">
                        <div
                            class="absolute top-4 -translate-x-3 whitespace-nowrap text-xs"
                            :style="{
                                left: `${pos}%`,
                            }"
                        >
                            <div
                                class="cursor-pointer rounded p-2 px-2 py-0.5 uppercase"
                                :class="[
                                    getGradeAndColorsFromPercent(label).textColor,
                                    getGradeAndColorsFromPercent(label).bgPrimaryColor,
                                    {
                                        'opacity-25': !active,
                                    },
                                ]"
                            >
                                {{ getGradeAndColorsFromPercent(label).letter }}
                            </div>
                        </div>
                    </template>
                </vue-slider>
            </div>

            <MultiSelect
                v-if="mode === 'tags'"
                v-model="selectedItems"
                mode="tags"
                :options="[]"
                :create-option="true"
                :searchable="true"
                :on-create="handleTagCreate"
            >
            </MultiSelect>

            <div
                v-else-if="!(selectMode === 'single' && selectedItemsByNameAlphabetically?.length > 0)"
                class="relative"
            >
                <MultiSelect
                    ref="multiselect"
                    v-model="selectedItems"
                    select-label=""
                    :mode="mode"
                    deselect-label=""
                    :placeholder="searchPlaceholder"
                    track-by="id"
                    label="label"
                    value-prop="id"
                    :attrs="{
                        autocomplete: 'off',
                    }"
                    :loading="isLoading"
                    :object="true"
                    :options="codesProvider"
                    :classes="styling"
                    :searchable="true"
                    :min-chars="0"
                    :delay="250"
                    :clear-on-search="true"
                    :clear-on-select="true"
                    :close-on-select="closeOnSelect"
                    :clear-on-blur="false"
                    :resolve-on-load="false"
                    :can-clear="false"
                    :hide-selected="true"
                    :no-results-text="noResultsText"
                    :filter-results="false"
                    :multiple-label="multipleLabelHandler"
                    :block-keys="['Delete']"
                    :caret="true"
                    no-options-text="No matching results found"
                    @change="onSelectionChange"
                    @keydown="onCodeInputKeyDown"
                    @close="resetDropdownScroll"
                    @open="handleInternalOpen"
                >
                    <template #caret>
                        <div>
                            <div v-if="isLoading" class="absolute right-0 top-0 mr-3 flex h-full">
                                <AppIcon
                                    name="spinner-third"
                                    class="my-auto h-3.5 w-3.5 animate-spin text-slate-500"
                                />
                            </div>
                            <div
                                v-else
                                class="absolute right-0 top-0 mr-3 flex h-full"
                                @mousedown.stop.prevent="toggleMultiSelect()"
                            >
                                <AppIcon
                                    name="chevron-down"
                                    class="my-auto h-3 w-3 text-slate-500"
                                />
                            </div>
                        </div>
                    </template>

                    <!-- <template #spinner>
                        <div class="absolute right-0 top-0 mr-3 flex h-full">
                            <AppIcon name="spinner-third" class="my-auto h-3.5 w-3.5 text-slate-500 animate-spin" />
                        </div>
                    </template> -->

                    <template #option="{ option }">
                        <div class="relative flex w-full">
                            <template v-if="codeType === 'games_list' || codeType === 'game'">
                                <div class="flex w-full border-b border-slate-200 px-3">
                                    <GameThumbnail
                                        :platform="getThumbnailPlatform(option)"
                                        :game-id="getThumbnailGameId(option)"
                                        :game-name="option.label"
                                        :thumbnail-url="option.thumbnail_url"
                                        :thumbnail-size="getThumbnailSize(option)"
                                        class="my-auto h-7 w-12"
                                    />
                                    <div class="ml-2 flex flex-1 items-center justify-between py-2">
                                        <div>
                                            <div class="mb-1 text-sm font-medium leading-none text-slate-900">
                                                <span>{{ option.label }}</span>
                                                <span
                                                    v-if="option?.metadata?.release_year"
                                                    class="text-xs leading-none text-slate-600"
                                                >
                                                    ({{ option.metadata.release_year }})
                                                </span>
                                            </div>
                                            <!-- <div
                                                v-if="
                                                    displayOptions &&
                                                    option.metric &&
                                                    displayOptions
                                                "
                                                class="text-xs leading-none text-slate-600"
                                            >
                                                {{
                                                    $filters.formatWholeNumberWithUnit(
                                                        option.metric,
                                                    )
                                                }}
                                                creators
                                            </div> -->
                                        </div>
                                        <!-- Platform availability indicators for IGDB games -->
                                        <div v-if="platform === 'igdb' && (codeType === 'game' || codeType === 'games_list') && option._platforms" class="ml-2 flex items-center gap-1.5">
                                            <AppIcon
                                                v-if="option._platforms.twitch?.id && typeof option._platforms.twitch.id === 'string'"
                                                name="twitch"
                                                class="h-3.5 w-3.5 text-twitch"
                                                :title="'Available on Twitch'"
                                            />
                                            <AppIcon
                                                v-if="option._platforms.youtube?.id && typeof option._platforms.youtube.id === 'string'"
                                                name="youtube"
                                                class="h-3.5 w-3.5 text-youtube"
                                                :title="'Available on YouTube'"
                                            />
                                            <span
                                                v-if="(!option._platforms.twitch?.id || typeof option._platforms.twitch.id !== 'string') && (!option._platforms.youtube?.id || typeof option._platforms.youtube.id !== 'string')"
                                                class="text-xs text-slate-400"
                                                :title="'Not available on Twitch or YouTube'"
                                            >
                                                —
                                            </span>
                                        </div>
                                    </div>
                                </div>
                            </template>
                            <template v-else>
                                <div class="flex w-full border-b border-slate-200 px-3">
                                    <div class="flex-col py-2">
                                        <div class="w-full font-medium text-slate-900">
                                            {{ getCodeOptionDisplayLabel(option) }}
                                        </div>
                                    </div>
                                </div>
                            </template>
                        </div>
                    </template>
                </MultiSelect>
            </div>
        </div>
    </div>
</template>

<script>
import _ from 'lodash'
import { mapGetters } from 'vuex'
import VueSlider from 'vue-slider-component'
import MultiSelect from '@vueform/multiselect'
import { getGradeAndColorsFromPercent } from '@/utils/affinity'
import GameThumbnail from '@/components/GameThumbnail.vue'

export default {
    name: 'CodeSelect',

    components: {
        MultiSelect,
        VueSlider,
        GameThumbnail,
    },

    props: {
        inputType: { type: String, default: 'select-code-multiple' },
        selectedOptions: {},
        title: { type: String },
        codeType: { type: String },
        platform: { type: String },
        preload: { type: Boolean },
        maxItems: { type: Number },
        searchPlaceholder: { type: String },
        multipleLabel: { type: String },
        selectMode: { type: String, default: 'multiple' },
        isCaretEnabled: { type: Boolean, default: false },
        noResultsText: { type: String, default: 'All matches selected' },
        sliderOptions: { type: Object },
        displayOptions: { type: Object },
        idField: {
            type: String,
            default: () => 'id',
        },
        currentFiltersContext: { type: Object },
        mode: { type: String, default: 'multiple' },
        disabled: { type: Boolean, default: false },
        open: { type: Boolean, default: undefined },
    },

    emits: ['selection-change', 'update:open'],

    data() {
        return {
            selectedItems: [],



            isLoading: false,

            styling: {
                container: `
                    form-select relative mx-auto w-full flex items-center justify-end box-border cursor-pointer border border-slate-200
                    rounded bg-white text-sm leading-snug outline-none py-0 hover:border-slate-300 shadow-none
                `,
                containerDisabled: 'cursor-default bg-slate-100',
                containerOpen: 'rounded-b-none',
                containerOpenTop: 'rounded-t-none',
                containerActive: 'border-slate-300',
                singleLabel:
                    'flex items-center h-full max-w-full absolute left-0 top-0 pointer-events-none bg-transparent leading-snug pl-3.5 pr-16 box-border rtl:left-auto rtl:right-0 rtl:pl-0 rtl:pr-3.5',
                singleLabelText: 'overflow-ellipsis overflow-hidden block whitespace-nowrap max-w-full',
                multipleLabel:
                    'flex items-center h-full absolute left-0 top-0 pointer-events-none bg-transparent leading-snug pl-3.5 rtl:left-auto rtl:right-0 rtl:pl-0 rtl:pr-3.5',
                search: `
                    w-full absolute inset-0 outline-none focus:ring-0 appearance-none box-border border-0 text-base
                    font-sans bg-white rounded pl-3.5 rtl:pl-0 rtl:pr-3.5 py-0 hover:border-slate-300 focus:border-slate-300
                `,
                tags: 'flex-grow flex-shrink flex flex-wrap items-center mt-1 pl-2 rtl:pl-0 rtl:pr-2',
                tag: `
                    bg-indigo-500 text-white text-sm font-semibold py-0.5 pl-2 rounded mr-1 mb-1 flex items-center
                    rtl:pl-0 rtl:pr-2 rtl:mr-0 rtl:ml-1
                `,
                tagDisabled: 'pr-2 opacity-50 rtl:pl-2',
                tagRemove:
                    'flex items-center justify-center p-1 mx-0.5 rounded-sm hover:bg-black hover:bg-opacity-10 group',
                tagRemoveIcon:
                    'bg-multiselect-remove bg-center bg-no-repeat opacity-30 inline-block w-3 h-3 group-hover:opacity-60',
                tagsSearchWrapper: 'inline-block relative mx-1 mb-1 flex-grow flex-shrink h-full',
                tagsSearch:
                    'absolute inset-0 border-0 outline-none focus:ring-0 appearance-none p-0 text-base font-sans box-border w-full',
                tagsSearchCopy: 'invisible whitespace-pre-wrap inline-block h-px',
                placeholder:
                    'flex items-center h-full absolute left-0 top-0 pointer-events-none bg-transparent leading-snug pl-3.5 text-slate-400 rtl:left-auto rtl:right-0 rtl:pl-0 rtl:pr-3.5',
                caret: 'bg-multiselect-caret bg-center bg-no-repeat w-2.5 h-4 py-px box-content mr-3.5 relative z-10 opacity-40 flex-shrink-0 flex-grow-0 transition-transform transform pointer-events-none rtl:mr-0 rtl:ml-3.5',
                caretOpen: 'rotate-180 pointer-events-auto',
                clear: 'pr-3.5 relative z-10 opacity-40 transition duration-300 flex-shrink-0 flex-grow-0 flex hover:opacity-80 rtl:pr-0 rtl:pl-3.5',
                clearIcon: 'bg-multiselect-remove bg-center bg-no-repeat w-2.5 h-4 py-px box-content inline-block',
                spinner:
                    'bg-multiselect-spinner bg-center bg-no-repeat w-4 h-4 z-10 mr-3.5 animate-spin flex-shrink-0 flex-grow-0 rtl:mr-0 rtl:ml-3.5',
                infinite: 'flex items-center justify-center w-full',
                infiniteSpinner:
                    'bg-multiselect-spinner bg-center bg-no-repeat w-4 h-4 z-10 animate-spin flex-shrink-0 flex-grow-0 m-3.5',
                dropdown: `codeselect-dropdown max-h-[min(18rem,calc(100vh-1rem))] absolute -left-px -right-px bottom-0 transform translate-y-full border border-slate-300 -mt-px
                    overflow-y-auto z-50 bg-white flex flex-col rounded-b
                     scrollbar scrollbar-track-slate-200 scrollbar-thumb-slate-400 scrollbar-w-2 scrollbar-h-2 hover:scrollbar-thumb-slate-500 active:scrollbar-thumb-slate-600`,
                dropdownTop: '-translate-y-full top-px bottom-auto rounded-b-none rounded-t',
                dropdownHidden: 'hidden',
                options: 'flex flex-col p-0 m-0 list-none',
                optionsTop: '',
                group: 'p-0 m-0',
                groupLabel:
                    'flex text-sm box-border items-center justify-start text-left py-1 px-3 font-semibold bg-slate-200 cursor-default leading-normal',
                groupLabelPointable: 'cursor-pointer',
                groupLabelPointed: 'bg-slate-300 text-slate-700',
                groupLabelSelected: 'bg-indigo-600 text-white',
                groupLabelDisabled: 'bg-slate-100 text-slate-300 cursor-not-allowed',
                groupLabelSelectedPointed: 'bg-indigo-600 text-white opacity-90',
                groupLabelSelectedDisabled: 'text-indigo-100 bg-indigo-600 bg-opacity-50 cursor-not-allowed',
                groupOptions: 'p-0 m-0',

                option: `
                    flex items-center justify-start box-border text-left cursor-pointer text-sm leading-snug
                `,
                optionPointed: 'text-slate-800 bg-slate-100',
                optionSelected: 'bg-slate-200',
                optionDisabled: 'text-slate-300 cursor-not-allowed',
                optionSelectedPointed: 'bg-slate-300',
                optionSelectedDisabled: 'text-indigo-100 bg-indigo-500 bg-opacity-50 cursor-not-allowed',
                noOptions: 'py-2 px-3 text-slate-600 bg-white text-left cursor-default',
                noResults: 'py-2 px-3 text-slate-600 bg-white text-left cursor-default',
                fakeInput:
                    'bg-transparent absolute left-0 right-0 -bottom-px w-full h-px border-0 p-0 appearance-none outline-none text-transparent',
                spacer: 'h-9 py-px box-content',
            },
            getGradeAndColorsFromPercent,
            sliderValue: null,

            localValue: null,
            isMultiselectOpen: false,
        }
    },

    computed: {
        ...mapGetters(['getFromDictionary']),

        selectedItemsByNameAlphabetically() {
            let selectedItems = []

            if (this.selectedItems?.length > 0) {
                selectedItems = _(this.selectedItems)
                    .cloneDeep()
                    .map((item) =>
                        _.assign(item, {
                            // Sometimes older saved filters don't have the label field
                            label: item.label ? item.label : item.id,
                        }),
                    )
            }

            return _.orderBy(selectedItems, ['name'], ['asc'])
        },

        closeOnSelect() {
            return this.selectMode === 'single'
        },
    },

    watch: {
        open(newVal) {
            if (newVal === false) {
                this.$refs.multiselect?.close()
            }
        },

        selectedOptions(newValue) {
            // console.log('selectedOptions', this.codeType, this.inputType, newValue)

            if (this.inputType === 'dropdown-code-single') {
                if (newValue && newValue.length) {
                    this.localValue = newValue
                } else {
                    this.localValue = null
                }
            } else if (this.inputType === 'select-code-multiple') {
                if (newValue && newValue.length) {
                    this.selectedItems = newValue
                } else {
                    this.selectedItems = []
                }
            }
        },

        async platform(newValue, oldValue) {
            if (newValue && newValue !== oldValue) {
                // console.log(`Platform updated`, newValue, this.codeType)

                if (this.inputType === 'dropdown-code-single') {
                    this.localValue = this.selectedOptions
                    await this.loadOptionsForDropdownSingle()
                } else if (this.inputType === 'select-code-multiple') {
                    // Only refresh options if this component is currently visible
                    // (i.e., its parent group is expanded)
                    if (this.isComponentVisible()) {
                        this.$refs?.multiselect?.refreshOptions(() => {
                            // console.log(`Multiselect options refreshed for visible component`, this.codeType)
                        })
                    }
                }
            }
        },

        localValue(newValue, oldValue) {
            if (newValue !== oldValue) {
                this.$emit('selection-change', newValue)
            }
        },

        'currentFiltersContext.classifications.hierarchy.topic': {
            async handler(newValue, oldValue) {
                if (this.codeType === 'classifications_category' && newValue !== oldValue) {
                    if (this.inputType === 'dropdown-code-single') {
                        this.localValue = null
                        await this.loadOptionsForDropdownSingle()
                    } else if (this.inputType === 'select-code-multiple') {
                        this.selectedItems = []
                        this.$emit('selection-change', [])
                        this.$refs?.multiselect?.refreshOptions(() => {
                            console.log('Multiselect options refreshed for category after topic change')
                        })
                    }
                }
            },
            deep: true,
            immediate: false,
        },
    },

    async created() {
        if (this.inputType === 'dropdown-code-single') {
            // Set initial value but don't load options yet (lazy loading)
            if (!this.selectedOptions || this.selectedOptions.length === 0) {
                this.localValue = null
            } else {
                this.localValue = this.selectedOptions
            }
        } else if (this.inputType === 'select-code-multiple') {
            if (this.selectMode === 'single') {
                this.selectedItems = this.selectedOptions
            } else if (this.selectedOptions && this.selectedOptions.length) {
                this.selectedItems = this.selectedOptions
            }

            if (this.sliderOptions) {
                this.sliderValue = this.sliderOptions.defaultValue
            }
        }
    },

    async mounted() {
        // If preload is enabled, trigger an initial load of options
        if (this.preload && this.inputType === 'select-code-multiple' && this.$refs.multiselect) {
            // Trigger initial options load by refreshing the multiselect
            await this.$nextTick()
            this.$refs.multiselect?.refreshOptions(() => {
                // Options loaded
            })
        }
    },

    methods: {
        toggleMultiSelect() {
            const select = this.$refs.multiselect
            if (!select) return

            if (this.open) {
                select.close()
                this.$emit('update:open', false)
            } else {
                select.open()
                this.$nextTick(this.updateDropdownPlacement)
                this.$emit('update:open', true)
            }
        },

        updateDropdownPlacement() {
            const selectEl = this.$refs.multiselect?.$el
            if (!selectEl) return

            const viewportPadding = 8
            const preferredDropdownHeight = 288
            const minimumDropdownHeight = 120
            const dropdown = selectEl.querySelector('.codeselect-dropdown')
            if (!dropdown) return

            const rect = selectEl.getBoundingClientRect()
            const spaceBelow = window.innerHeight - rect.bottom - viewportPadding
            const spaceAbove = rect.top - viewportPadding
            const dropdownHeight = dropdown.offsetHeight || preferredDropdownHeight
            const shouldOpenAbove = spaceBelow < dropdownHeight && spaceAbove > spaceBelow
            const availableHeight = Math.max(
                minimumDropdownHeight,
                Math.min(preferredDropdownHeight, shouldOpenAbove ? spaceAbove : spaceBelow),
            )

            dropdown.style.maxHeight = `${availableHeight}px`
            dropdown.style.top = shouldOpenAbove ? '1px' : ''
            dropdown.style.bottom = shouldOpenAbove ? 'auto' : '0'
            dropdown.style.transform = shouldOpenAbove ? 'translateY(-100%)' : 'translateY(100%)'
        },

        async codesProvider(searchText) {
            if (
                this.codeType === 'classifications_category' &&
                !this.currentFiltersContext?.classifications?.hierarchy?.topic
            ) {
                return []
            }

            this.isLoading = true

            try {
                const results = await this.$store.dispatch('fetchFilterCodes', {
                    platform: this.platform,
                    codeType: this.codeType,
                    currentFiltersContext: this.currentFiltersContext,
                    displayOptions: this.displayOptions,
                    searchText,
                })

                this.isLoading = false
                return results
            } catch (error) {
                this.isLoading = false
                console.error('Error in codesProvider:', error)
                return []
            }
        },

        async loadOptionsForDropdownSingle(searchText) {
            if (this.inputType === 'dropdown-code-single') {
                if (this.$refs.multiselect?.refreshOptions) {
                    return this.$refs.multiselect.refreshOptions(() => {
                        this.$refs.multiselect?.refreshLabels?.()
                    })
                }
                return this.codesProvider(searchText)
            }
            return []
        },

        onRemoveSelection(value) {
            // this.$refs.multiselect.deselect({ value })
            _.forEach(this.selectedItems, (item) => {
                if (item.id === value.id) {
                    item.value2 = null
                }
            })
            this.selectedItems = _.filter(this.selectedItems, (item) => item.id !== value.id)
            this.$emit('selection-change', this.selectedItems)
        },

        multipleLabelHandler() {
            if (this.multipleLabel) return this.multipleLabel
            return 'Type to add more'
        },

        hasDropdownSingleSelection() {
            return this.inputType === 'dropdown-code-single' && this.localValue !== null && this.localValue !== undefined && this.localValue !== ''
        },

        getDropdownSinglePlaceholder() {
            if (this.disabled) {
                return 'Select a niche first'
            }

            if (this.isMultiselectOpen) {
                return this.searchPlaceholder || `Search ${this.title?.toLowerCase() || 'options'}`
            }

            if (this.hasDropdownSingleSelection()) {
                return this.localValue
            }

            if (this.codeType === 'classifications_topic') {
                return 'All niches'
            }

            if (this.codeType === 'classifications_category') {
                return 'All categories'
            }

            return this.searchPlaceholder
        },

        isComponentVisible() {
            // Check if this component's DOM element is visible
            // This happens when the parent group is expanded
            return this.$el && this.$el.offsetParent !== null
        },

        onSelectionChange(selectedValues) {
            // console.log('onSelectionChange', selectedValues)

            if (this.sliderValue) {
                _.forEach(selectedValues, (selectedVal) => {
                    if (!selectedVal.value2) {
                        selectedVal.value2 = this.sliderValue
                    }
                })
            }

            this.$emit('selection-change', selectedValues)
        },

        getCodeOptionDisplayLabel(option) {
            if (!option) return ''
            if (
                this.codeType === 'alternate_platforms' &&
                [option.id, option.label].some((value) => String(value || '').toLowerCase() === 'twitter')
            ) {
                return 'X'
            }

            return option.label || option.id || ''
        },

        onCodeInputKeyDown(event, select) {
            if (event.keyCode === 8 && ((select.search && select.search.length === 0) || !select.search)) {
                select.search = ' '
            }
        },

        handleInternalOpen() {
            this.isMultiselectOpen = true
            if (this.inputType === 'dropdown-code-single') {
                this.loadOptionsForDropdownSingle()
            }
            this.$nextTick(this.updateDropdownPlacement)
            this.$emit('update:open', true)
        },

        resetDropdownScroll() {
            this.isMultiselectOpen = false

            // Find the dropdown element within the multiselect component
            if (this.$refs.multiselect && this.$refs.multiselect.$el) {
                if (this.inputType === 'dropdown-code-single') {
                    this.$refs.multiselect.clearSearch?.()
                }

                const dropdown = this.$refs.multiselect.$el.querySelector('.scrollbar')

                if (dropdown) {
                    // Reset scroll position
                    dropdown.scrollTop = 0
                }
            }

            this.$emit('update:open', false)
        },

        getThumbnailPlatform(option) {
            return option?.thumbnail_platform || this.platform
        },

        getThumbnailGameId(option) {
            return option?.thumbnail_game_id || option?.id
        },

        getThumbnailSize(option) {
            return this.getThumbnailPlatform(option) === 'igdb' ? 'small' : 'default'
        },
    },
}
</script>
